import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Check, FileText, ShieldCheck, UserRound } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { DataKeys } from "../../../types";
import { getHealthModulePanelMotion } from "../../tools/Animations";
import {
    getLocalized,
    HealthLang,
    LocalizedString,
} from "../healthTypes";
import {
    healthClinicalAgentsActivePanel,
    healthClinicalAgentsCapabilitiesBlock,
    healthClinicalAgentsCapabilitiesLabel,
    healthClinicalAgentsDetailSummary,
    healthClinicalAgentsDetailTitle,
    healthClinicalAgentsIoBlock,
    healthClinicalAgentsIoBlockLabel,
    healthClinicalAgentsIoBlockLabelRow,
    healthClinicalAgentsIoCheck,
    healthClinicalAgentsIoGrid,
    healthClinicalAgentsIoItem,
    healthClinicalAgentsIoItemText,
    healthClinicalAgentsIoList,
    healthClinicalAgentsIoSection,
    healthClinicalAgentsReviewHighlight,
    healthClinicalAgentsReviewIcon,
    healthClinicalAgentsReviewLabel,
    healthClinicalAgentsReviewText,
    healthClinicalAgentsStageBody,
    healthClinicalAgentsStageDetails,
    healthClinicalAgentsStageImage,
    healthClinicalAgentsStageImageWrap,
    healthClinicalAgentsStageIntro,
    healthClinicalAgentsStageLabel,
    healthClinicalAgentsStageLeftCol,
    healthClinicalAgentsSummaryLabel,
    healthClinicalAgentsSummaryPanel,
    healthClinicalAgentsTag,
    healthClinicalAgentsTagsRow,
    healthClinicalAgentsWorkflowLabel,
    healthClinicalAgentsWorkflowNote,
    healthClinicalAgentsWorkflowText,
} from "../healthVisualStyles";

interface HealthClinicalAgentDetailPanelProps {
    block: DataKeys;
    lang: HealthLang;
    transitionDirection?: number;
    clinicalInputsLabel: string;
    outputsLabel: string;
    physicianReviewLabel: string;
    workflowBenefitLabel: string;
    summaryLabel: string;
    capabilitiesLabel: string;
    panelId: string;
    tabId: string;
    previewSrc: string;
    previewAlt: string;
}

function IoBlock({
    label,
    items,
    lang,
    icon: Icon,
}: {
    label: string;
    items: DataKeys[];
    lang: HealthLang;
    icon: LucideIcon;
}) {
    return (
        <div className={healthClinicalAgentsIoBlock}>
            <div className={healthClinicalAgentsIoBlockLabelRow}>
                <Icon
                    className="h-4 w-4 shrink-0 text-teal-600"
                    strokeWidth={2}
                    aria-hidden
                />
                <p className={healthClinicalAgentsIoBlockLabel}>{label}</p>
            </div>
            <ul className={healthClinicalAgentsIoList}>
                {items.map((item, index) => (
                    <li key={index} className={healthClinicalAgentsIoItem}>
                        <span
                            className={healthClinicalAgentsIoCheck}
                            aria-hidden="true"
                        >
                            <Check className="h-2.5 w-2.5" strokeWidth={2.5} />
                        </span>
                        <span className={healthClinicalAgentsIoItemText}>
                            {getLocalized(item as LocalizedString, lang)}
                        </span>
                    </li>
                ))}
            </ul>
        </div>
    );
}

export function HealthClinicalAgentDetailPanel({
    block,
    lang,
    transitionDirection = 1,
    clinicalInputsLabel,
    outputsLabel,
    physicianReviewLabel,
    workflowBenefitLabel,
    summaryLabel,
    capabilitiesLabel,
    panelId,
    tabId,
    previewSrc,
    previewAlt,
}: HealthClinicalAgentDetailPanelProps) {
    const productId = block.id as string;
    const reduceMotion = useReducedMotion();
    const panelMotion = getHealthModulePanelMotion(
        transitionDirection,
        reduceMotion
    );
    const tags = (block.tags as DataKeys[] | undefined) ?? [];

    return (
        <motion.article
            layout
            transition={{ layout: { duration: 0.25, ease: [0.22, 1, 0.36, 1] } }}
            id={panelId}
            role="tabpanel"
            aria-labelledby={tabId}
            className={`${healthClinicalAgentsActivePanel} scroll-mt-24 overflow-hidden`}
        >
            <AnimatePresence mode="wait" initial={false}>
                <motion.div
                    key={productId}
                    initial={panelMotion.initial}
                    animate={panelMotion.animate}
                    exit={panelMotion.exit}
                    transition={panelMotion.transition}
                    className={healthClinicalAgentsStageBody}
                >
                <div className={healthClinicalAgentsStageLeftCol}>
                    <header className={healthClinicalAgentsStageIntro}>
                        <p className={healthClinicalAgentsStageLabel}>
                            {getLocalized(
                                block.stageLabel as LocalizedString,
                                lang
                            )}
                        </p>
                        <h3 className={healthClinicalAgentsDetailTitle}>
                            {getLocalized(block.title as LocalizedString, lang)}
                        </h3>
                        <p className={healthClinicalAgentsDetailSummary}>
                            {getLocalized(
                                block.function as LocalizedString,
                                lang
                            )}
                        </p>
                    </header>

                    <div className={healthClinicalAgentsStageImageWrap}>
                        <img
                            src={previewSrc}
                            alt={previewAlt}
                            className={healthClinicalAgentsStageImage}
                            loading="lazy"
                            decoding="async"
                        />
                    </div>
                </div>

                <div className={healthClinicalAgentsStageDetails}>
                    <div className={healthClinicalAgentsSummaryPanel}>
                        <p className={healthClinicalAgentsSummaryLabel}>
                            {summaryLabel}
                        </p>

                        <div className={healthClinicalAgentsIoSection}>
                            <div className={healthClinicalAgentsIoGrid}>
                            <IoBlock
                                label={clinicalInputsLabel}
                                items={block.clinicalInputs as DataKeys[]}
                                lang={lang}
                                icon={UserRound}
                            />
                            <IoBlock
                                label={outputsLabel}
                                items={block.outputs as DataKeys[]}
                                lang={lang}
                                icon={FileText}
                            />
                            </div>
                        </div>

                        <div className={healthClinicalAgentsReviewHighlight}>
                            <span
                                className={healthClinicalAgentsReviewIcon}
                                aria-hidden="true"
                            >
                                <ShieldCheck
                                    className="h-8 w-8 lg:h-9 lg:w-9"
                                    strokeWidth={2}
                                />
                            </span>
                            <div className="min-w-0 flex-1">
                                <p className={healthClinicalAgentsReviewLabel}>
                                    {physicianReviewLabel}
                                </p>
                                <p className={healthClinicalAgentsReviewText}>
                                    {getLocalized(
                                        block.physicianControl as LocalizedString,
                                        lang
                                    )}
                                </p>
                            </div>
                        </div>

                        <div className={healthClinicalAgentsWorkflowNote}>
                            <div className="min-w-0 flex-1">
                                <p className={healthClinicalAgentsWorkflowLabel}>
                                    {workflowBenefitLabel}
                                </p>
                                <p className={healthClinicalAgentsWorkflowText}>
                                    {getLocalized(
                                        block.workflowBenefit as LocalizedString,
                                        lang
                                    )}
                                </p>
                            </div>
                        </div>

                        {tags.length > 0 ? (
                            <div className={healthClinicalAgentsCapabilitiesBlock}>
                                <p
                                    className={
                                        healthClinicalAgentsCapabilitiesLabel
                                    }
                                >
                                    {capabilitiesLabel}
                                </p>
                                <div
                                    className={healthClinicalAgentsTagsRow}
                                    role="list"
                                    aria-label={capabilitiesLabel}
                                >
                                    {tags.map((tag, index) => (
                                        <span
                                            key={index}
                                            role="listitem"
                                            className={healthClinicalAgentsTag}
                                        >
                                            {getLocalized(
                                                tag as LocalizedString,
                                                lang
                                            )}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        ) : null}
                    </div>
                </div>
                </motion.div>
            </AnimatePresence>
        </motion.article>
    );
}
